import { MetaEventName, MetaPayload } from './marketing-events';

/**
 * Respuesta estándar de nuestras API Routes de Marketing
 */
export interface MetaTrackingResponse {
  ok: boolean;
  message?: string;
  mode?: 'test' | 'production';
  error?: string;
  details?: any;
}

export const MetaService = {
  /**
   * Obtiene la URL base para las peticiones de analíticas.
   * En desarrollo apunta al puerto 3001 (Public API).
   */
  getBaseUrl(): string {
    // 1. Entorno de Servidor (Node.js)
    if (typeof window === 'undefined') {
      return process.env.NODE_ENV === 'development'
        ? 'http://localhost:3001'
        : 'https://tufirma.pro';
    }

    // 2. Entorno de Navegador
    // Si estamos en localhost, forzamos 3001 para que el Dashboard (3000) pueda hablar con el Public API
    const isLocal =
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1';
    
    if (isLocal) return 'http://localhost:3001';

    // En producción, usamos la URL raíz (esto asume que el API está en el mismo dominio o manejado por el ingress)
    return 'https://tufirma.pro';
  },

  /**
   * TRACK ONLINE: Se usa en el Navegador (Landing, Catálogo, Botones).
   */
  async trackOnline(
    params: MetaPayload,
  ): Promise<MetaTrackingResponse | undefined> {
    try {
      const baseUrl = this.getBaseUrl();
      const response = await fetch(`${baseUrl}/api/marketing/online`, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });

      const data: MetaTrackingResponse = await response.json();

      if (!response.ok) {
        console.error(
          `[MetaService Online] Server returned ${response.status}:`,
          data.error,
        );
        return data;
      }

      return data;
    } catch (error) {
      console.error('[MetaService Online] Network/Runtime Error:', error);
      return { ok: false, error: 'Network error during online tracking' };
    }
  },

  /**
   * TRACK OFFLINE: Se usa en el Backend (Server Actions, Webhooks, Caja).
   */
  async trackOffline(
    params: MetaPayload,
  ): Promise<MetaTrackingResponse | undefined> {
    try {
      const baseUrl = this.getBaseUrl();
      const response = await fetch(`${baseUrl}/api/marketing/offline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      });
      const data: MetaTrackingResponse = await response.json();

      if (!response.ok) {
        console.error(
          `[MetaService Offline] Server returned ${response.status}:`,
          data.error,
        );
        return data;
      }

      return data;
    } catch (error) {
      console.error('[MetaService Offline] Network/Runtime Error:', error);
      return { ok: false, error: 'Network error during offline tracking' };
    }
  },

  /**
   * DIAGNÓSTICO: Prueba de fuego para la configuración de un salón.
   */
  async testConnection(config: {
    salonId: string;
    pixelId: string;
    accessToken: string;
    testCode: string;
  }): Promise<MetaTrackingResponse | undefined> {
    const testPayload: MetaPayload = {
      salonId: config.salonId,
      eventName: 'PageView' as MetaEventName,
      userData: {
        name: 'Test Connection User',
        phone: '584240000000',
      },
      customData: {
        value: 0,
        currency: 'USD',
        content_name: 'Diagnostic Connection Test',
      },
      eventId: `diag_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
      eventSourceUrl:
        typeof window !== 'undefined'
          ? window.location.href
          : 'https://tufirma.pro',
      testEventCode: config.testCode,
    };

    return this.trackOnline(testPayload);
  },

  /**
   * TRACK POSTHOG: Server-side capture for events (e.g. QR scans).
   * This uses the PostHog public capture API.
   */
  async trackPostHog(event: string, properties: Record<string, any>) {
    try {
      const POSTHOG_KEY =
        process.env.NEXT_PUBLIC_POSTHOG_KEY ||
        process.env.POSTHOG_KEY ||
        'phc_4HsvHcaMh8RssHsF7zYk0cLZGspZWg0bBMUenjoqa1s';
      const POSTHOG_HOST =
        process.env.NEXT_PUBLIC_POSTHOG_HOST ||
        process.env.POSTHOG_HOST ||
        'https://us.i.posthog.com';

      const payload = {
        api_key: POSTHOG_KEY,
        event: event,
        properties: {
          ...properties,
          $lib: 'tufirma-backend',
        }
      };

      const response = await fetch(`${POSTHOG_HOST}/capture/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        console.error('[MetaService:PostHog] Capture failed:', await response.text());
      }
    } catch (error) {
      console.error('[MetaService:PostHog] Network Error:', error);
    }
  },
};
